import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Flame, Info } from "lucide-react";

// Color scale: rate=null (no data) → gray, 0% → rose, 50% → amber, 100% → emerald
function cellColor(rate) {
  if (rate === null || rate === undefined) return { bg: "#F1F5F9", text: "#94A3B8", border: "#E2E8F0" };
  if (rate >= 90) return { bg: "#065F46", text: "white", border: "#047857" };
  if (rate >= 75) return { bg: "#10B981", text: "white", border: "#059669" };
  if (rate >= 50) return { bg: "#84CC16", text: "white", border: "#65A30D" };
  if (rate >= 25) return { bg: "#F59E0B", text: "white", border: "#D97706" };
  if (rate > 0)  return { bg: "#F87171", text: "white", border: "#DC2626" };
  return { bg: "#DC2626", text: "white", border: "#991B1B" };
}

export default function AttendanceHeatmap() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    api.get(`/analytics/attendance-heatmap?days=${days}`).then((r) => setData(r.data)).catch(() => {});
  }, [days]);

  const dayLabels = useMemo(() => data?.days || [], [data]);

  if (!data) return null;

  return (
    <Card className="card-elevated border-slate-200/70" data-testid="attendance-heatmap">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="text-lg font-black text-slate-900 flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-600" />
              خريطة حرارة الحضور — لكل معلم
            </div>
            <div className="text-xs text-slate-500 mt-0.5">كل خلية = نسبة الحضور اليومية لأطفال المعلم</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex bg-slate-100 rounded-lg p-1">
              {[14, 30, 60, 90].map((n) => (
                <button
                  key={n}
                  onClick={() => setDays(n)}
                  data-testid={`heatmap-range-${n}`}
                  className={`px-3 py-1 rounded-md text-xs font-black ${days === n ? "bg-white text-sky-700 shadow-sm" : "text-slate-600"}`}
                >
                  {n} يوم
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-3 flex-wrap mb-3 text-[11px] text-slate-500">
          <div className="flex items-center gap-1">
            <span className="w-4 h-3 rounded-sm inline-block" style={{ background: "#F1F5F9" }} /> بدون بيانات
          </div>
          <div className="flex items-center gap-1">
            <span className="w-4 h-3 rounded-sm inline-block" style={{ background: "#DC2626" }} /> 0%
          </div>
          <div className="flex items-center gap-1">
            <span className="w-4 h-3 rounded-sm inline-block" style={{ background: "#F59E0B" }} /> 25-50%
          </div>
          <div className="flex items-center gap-1">
            <span className="w-4 h-3 rounded-sm inline-block" style={{ background: "#84CC16" }} /> 50-75%
          </div>
          <div className="flex items-center gap-1">
            <span className="w-4 h-3 rounded-sm inline-block" style={{ background: "#10B981" }} /> 75-90%
          </div>
          <div className="flex items-center gap-1">
            <span className="w-4 h-3 rounded-sm inline-block" style={{ background: "#065F46" }} /> 90-100%
          </div>
        </div>

        {data.teachers.length === 0 ? (
          <div className="text-center py-10 text-slate-400 text-sm">لا يوجد معلمون</div>
        ) : (
          <div className="overflow-x-auto -mx-2 px-2 pb-2" dir="ltr">
            <div className="inline-block min-w-full">
              <div className="grid" style={{ gridTemplateColumns: `220px repeat(${dayLabels.length}, minmax(14px, 1fr)) 80px` }}>
                {/* Header row */}
                <div className="text-[11px] font-black text-slate-500 uppercase tracking-wider py-2" dir="rtl">المعلم</div>
                {dayLabels.map((d, i) => (
                  <div
                    key={d}
                    className="text-center text-[9px] font-bold text-slate-400 py-2 relative"
                    title={d}
                  >
                    {i % Math.ceil(dayLabels.length / 10) === 0 && (
                      <span className="absolute -top-1 left-1/2 -translate-x-1/2 whitespace-nowrap">
                        {d.slice(5)}
                      </span>
                    )}
                  </div>
                ))}
                <div className="text-[11px] font-black text-slate-500 uppercase tracking-wider py-2 text-center">الإجمالي</div>

                {/* Rows */}
                {data.teachers.map((t) => (
                  <TeacherRow key={t.id} t={t} onHover={setHover} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Hover tooltip */}
        {hover && (
          <div
            data-testid="heatmap-tooltip"
            className="fixed z-50 bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg pointer-events-none"
            style={{ left: hover.x + 12, top: hover.y + 12 }}
          >
            <div className="font-bold">{hover.teacher} · {hover.date}</div>
            <div className="text-emerald-300 mt-1">حاضر: {hover.data.present} · غياب: {hover.data.absent} · بعذر: {hover.data.excused}</div>
            {hover.data.missing > 0 && <div className="text-amber-300">لم يتم تسجيل: {hover.data.missing}</div>}
            <div className="mt-1 font-black">
              {hover.data.rate === null ? "لا توجد سجلات" : `نسبة الحضور: ${hover.data.rate}%`}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TeacherRow({ t, onHover }) {
  return (
    <>
      <div className="flex items-center gap-2 py-1.5 pe-2 min-w-0" dir="rtl" data-testid={`heatmap-teacher-${t.id}`}>
        <div className="min-w-0 flex-1">
          <div className="font-bold text-slate-800 text-sm truncate">{t.name}</div>
          <div className="text-[10px] text-slate-500 truncate">
            {t.children_count} طفل · تسجيل {t.consistency}%
          </div>
        </div>
      </div>
      {t.daily.map((d) => {
        const c = cellColor(d.rate);
        return (
          <div
            key={d.date}
            data-testid={`heatmap-cell-${t.id}-${d.date}`}
            onMouseEnter={(e) => onHover({ x: e.clientX, y: e.clientY, teacher: t.name, date: d.date, data: d })}
            onMouseLeave={() => onHover(null)}
            className="h-6 mx-[1px] my-[2px] rounded-sm cursor-help transition-transform hover:scale-125"
            style={{ background: c.bg, border: `1px solid ${c.border}` }}
            title={`${t.name} · ${d.date}: ${d.rate === null ? "بدون بيانات" : `${d.rate}%`}`}
          />
        );
      })}
      <div className="text-center py-1.5" dir="rtl">
        <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-black ${
          t.overall_rate >= 75 ? "bg-emerald-100 text-emerald-800"
          : t.overall_rate >= 50 ? "bg-amber-100 text-amber-800"
          : "bg-rose-100 text-rose-800"
        }`}>
          {t.overall_rate}%
        </span>
      </div>
    </>
  );
}
